import { useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { ArrowRight, ChevronRight, Users } from 'lucide-react';
import { CircularGauge } from '@/components/ui/circular-gauge';
import { OrgCard } from '@/features/OrgLayout/primitives/OrgCard';
import { OrgAvatar } from '@/features/OrgLayout/primitives/OrgAvatar';
import { Sparkline } from '@/features/OrgLayout/primitives/Sparkline';
import { useOrgBatchReview, useOrgTrends } from '@/lib/hooks/useOrg';
import { ReviewCyclePanel } from '@/features/Review';
import type { OrgBatchDetail, OrgStudentRow } from '@/lib/api/org';

function attentionReason(s: OrgStudentRow): string | null {
  if (s.readiness_band === 'needs_support') return 'Needs support';
  if (s.attendance_pct < 60) return 'Low attendance';
  if (s.completion_pct < 30) return 'Behind';
  return null;
}

export function BatchOverviewTab({
  batchId,
  data,
  onViewStudents,
}: {
  batchId: string;
  data: OrgBatchDetail;
  onViewStudents: () => void;
}) {
  const navigate = useNavigate();
  const trends = useOrgTrends();
  // The review cycle: what moved since last week. Placed on the overview
  // because a placement officer opening a batch is asking "does anything here
  // need me this week", and until now the page could only answer "here is the
  // current state of everyone".
  const {
    data: review,
    isLoading: reviewLoading,
    isError: reviewError,
    refetch: refetchReview,
  } = useOrgBatchReview(batchId);
  const { batch, students } = data;

  const readyCount = students.filter(
    (s) => s.readiness_band === 'product_ready' || s.readiness_band === 'service_ready',
  ).length;
  const readyPct = students.length ? Math.round((readyCount / students.length) * 100) : 0;

  const stats = useMemo(() => {
    if (students.length === 0) return { highest: 0, lowest: 0, completed: 0 };
    const pcts = students.map((s) => Math.round(s.completion_pct));
    return {
      highest: Math.max(...pcts),
      lowest: Math.min(...pcts),
      completed: students.filter((s) => s.completion_pct >= 100).length,
    };
  }, [students]);

  const activity = trends.data?.batches.find((b) => b.batch_id === batchId)?.series ?? [];
  const activityValues = activity.map((p) => p.value);

  const attention = students
    .map((s) => ({ s, reason: attentionReason(s) }))
    .filter((x): x is { s: OrgStudentRow; reason: string } => x.reason !== null)
    .slice(0, 5);

  const goToStudent = (userId: string) =>
    navigate({ to: '/org/batches/$batchId/students/$userId', params: { batchId, userId } });

  return (
    <div className="space-y-5">
      <OrgCard className="p-6">
        <ReviewCyclePanel
          review={review}
          loading={reviewLoading}
          error={reviewError}
          onRetry={() => void refetchReview()}
          onSelectStudent={(userId) =>
            void navigate({ to: '/org/batches/$batchId/students/$userId', params: { batchId, userId } })
          }
        />
      </OrgCard>

      {/* Three summary cards */}
      <div className="grid gap-5 md:grid-cols-3">
        <OrgCard className="flex flex-col justify-between p-6">
          <p className="text-sm font-medium text-[#6b7280]">Students Enrolled</p>
          <p className="mt-2 text-[40px] font-bold leading-none text-[#111827]">{batch.student_count}</p>
          <button
            type="button"
            onClick={onViewStudents}
            className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-[#ff5a1f]"
          >
            View all students <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </OrgCard>

        <OrgCard className="flex items-center justify-between p-6">
          <div>
            <p className="text-sm font-medium text-[#6b7280]">Batch Progress</p>
            <p className="mt-2 text-xs text-[#9ca3af]">Average completion</p>
          </div>
          <CircularGauge value={batch.avg_completion_pct} label="complete" color="#0f766e" size={88} />
        </OrgCard>

        <OrgCard className="flex flex-col justify-between p-6">
          <p className="text-sm font-medium text-[#6b7280]">Placement Ready</p>
          <p className="mt-2 text-[40px] font-bold leading-none text-[#111827]">{readyPct}%</p>
          <p className="mt-3 text-xs text-[#9ca3af]">{readyCount} of {students.length} students</p>
        </OrgCard>
      </div>

      {/* Weekly activity + summary stats */}
      <OrgCard className="p-6">
        <div className="flex items-start justify-between gap-6">
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-semibold text-[#111827]">Weekly activity</h3>
            <p className="mt-1 text-xs text-[#9ca3af]">Submissions &amp; topic completions per week</p>
            <div className="mt-4">
              {activityValues.length >= 2 ? (
                <Sparkline values={activityValues} stroke="#0f766e" width={420} height={90} className="w-full" />
              ) : (
                <p className="py-10 text-center text-sm text-[#9ca3af]">Not enough activity yet.</p>
              )}
            </div>
          </div>
          <div className="grid w-[220px] flex-shrink-0 grid-cols-2 gap-4">
            <Stat label="Average" value={`${Math.round(batch.avg_completion_pct)}%`} tone="#111827" />
            <Stat label="Highest" value={`${stats.highest}%`} tone="#0f766e" />
            <Stat label="Lowest" value={`${stats.lowest}%`} tone="#ff5a1f" />
            <Stat label="Completed" value={`${stats.completed}/${students.length}`} tone="#111827" />
          </div>
        </div>
      </OrgCard>

      {/* Students needing attention */}
      <OrgCard className="p-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-[#111827]">Students Needing Attention</h3>
          <button type="button" onClick={onViewStudents} className="inline-flex items-center gap-1 text-sm font-medium text-[#ff5a1f]">
            View all students <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
        {attention.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-8 text-center">
            <Users className="h-8 w-8 text-[#9ca3af]" />
            <p className="text-sm text-[#6b7280]">Nothing flagged — nice.</p>
          </div>
        ) : (
          <ul className="mt-4 space-y-1">
            {attention.map(({ s, reason }) => (
              <li key={s.user_id}>
                <button
                  type="button"
                  onClick={() => goToStudent(s.user_id)}
                  className="flex w-full items-center gap-3 rounded-xl px-2 py-2 text-left hover:bg-[#fafafa]"
                >
                  <OrgAvatar name={s.name} size={36} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-[#111827]">{s.name}</p>
                    <p className="truncate text-xs text-[#9ca3af]">{Math.round(s.completion_pct)}% complete · {Math.round(s.attendance_pct)}% attendance</p>
                  </div>
                  <span className="rounded-full bg-[#fff1ec] px-2.5 py-1 text-xs font-medium text-[#ff5a1f]">{reason}</span>
                  <ChevronRight className="h-4 w-4 flex-shrink-0 text-[#9ca3af]" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </OrgCard>
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone: string }) {
  return (
    <div>
      <p className="text-xl font-bold leading-none" style={{ color: tone }}>{value}</p>
      <p className="mt-1 text-xs text-[#9ca3af]">{label}</p>
    </div>
  );
}
